/**
 * THE GUIDE MODEL: what a guide's filters and search mean, with nothing drawn.
 * views/guide-reader.js paints the result; Help search and Ask Track's quick
 * reference link read it too. Shape: docs/regs-reference/GUIDE-SCHEMA.md.
 */

/** Every block kind the reader draws (test/guide-model.test.mjs holds guides to it). */
export const BLOCK_KINDS = ['prose', 'kv', 'table', 'facts', 'list', 'figure', 'reco', 'facets', 'cards', 'numbers', 'timeline', 'sources'];

/** Per device: the chair a person reads from, across every guide that has it. */
export const PERSP_KEY = 'track-guide-persp';

const isPlainObject = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
// A perspective map is an object whose keys are all perspective ids. It becomes
// the active view's value; a missing or null value means "not in this view".
function resolveValue(v, ids, persp) {
  if (Array.isArray(v)) return v.map((x) => resolveValue(x, ids, persp));
  if (!isPlainObject(v)) return v;
  const keys = Object.keys(v);
  if (keys.length && keys.every((k) => ids.includes(k))) return resolveValue(v[persp] ?? null, ids, persp);
  return Object.fromEntries(keys.map((k) => [k, resolveValue(v[k], ids, persp)]));
}

/**
 * One guide as read from one chair: every perspective map resolved, the sections
 * of other chairs dropped and the rest numbered (`n`), and a jump to a page this
 * view doesn't have removed. A guide with no perspectives is returned as it is.
 * An unknown perspective reads as the guide's first.
 */
export function resolveGuide(guide, perspective) {
  const ids = (guide.perspectives || []).map((p) => p.id);
  if (!ids.length) return guide;
  const persp = ids.includes(perspective) ? perspective : ids[0];
  const g = resolveValue(guide, ids, persp);
  g.perspective = persp;
  g.sections = g.sections
    .filter((s) => !s.perspectives || s.perspectives.includes(persp))
    .map((s, i) => ({
      ...s,
      n: i + 1,
      blocks: (s.blocks || []).filter(Boolean).map((b) => (b.kind !== 'cards' ? b : {
        ...b,
        items: b.items.map((c) => (!c.body?.actions ? c : {
          ...c,
          body: { ...c.body, actions: c.body.actions.filter((a) => a.kind !== 'jump' || a.section) },
        })),
      })),
    }));
  return g;
}

/** Which chair to read a guide from: the URL's, else this device's, else its first. */
export function pickPerspective(guide, { view = null, saved = null } = {}) {
  const ids = (guide.perspectives || []).map((p) => p.id);
  return [view, saved].find((x) => ids.includes(x)) || ids[0] || null;
}

/** The hero's counts: what the guide states; if it states none, counted from it. */
export function heroStats(guide) {
  if (guide.hero?.stats) return guide.hero.stats;
  const count = (kind, of) => allBlocks(guide, kind).reduce((n, { block }) => n + of(block).length, 0);
  return [
    [count('cards', (b) => b.items), 'cards'],
    [count('numbers', (b) => b.rows), 'thresholds'],
    [(guide.facets?.chips?.options || []).length, 'project types'],
    [count('timeline', (b) => b.rows), 'dated changes'],
  ].filter(([n]) => n).map(([n, l]) => [String(n), l]);
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** '2026-11-03' → '3 Nov 2026'. Text only: nothing here compares with today. */
export function fmtGuideDate(iso) {
  const [y, m, d] = String(iso || '').split('-');
  return y && m && d ? `${Number(d)} ${MON[Number(m) - 1]} ${y}` : '';
}

const allBlocks = (guide, kind) => (guide.sections || []).flatMap((s) => (s.blocks || []).filter((b) => b.kind === kind).map((block) => ({ section: s, block })));

/** The filters a guide starts from: what was saved, where it still means something. */
export function guideState(guide, saved) {
  const f = guide.facets;
  const s = saved && typeof saved === 'object' ? saved : {};
  const out = { q: typeof s.q === 'string' ? s.q : '' };
  if (f?.segment) {
    const opts = f.segment.options;
    out[f.segment.key] = opts.some((o) => o.id === s[f.segment.key]) ? s[f.segment.key] : opts[0].id;
  }
  if (f?.chips) out[f.chips.key] = f.chips.options.some((o) => o.id === s[f.chips.key]) ? s[f.chips.key] : null;
  return out;
}

const words = (q) => String(q || '').toLowerCase().split(/\s+/).filter(Boolean);
// AND-word: every word somewhere in the text. A one-character word must stand
// alone, so the "m" of "4.5 m" is the unit rather than every m on the page.
function matches(text, ws) {
  if (!ws.length) return true;
  const low = String(text || '').toLowerCase();
  const tokens = ws.some((w) => w.length === 1) ? low.split(/[^\p{L}\p{N}.]+/u) : [];
  return ws.every((w) => (w.length === 1 ? tokens.includes(w) : low.includes(w)));
}
const cellText = (c) => (c && typeof c === 'object' ? (c.tags || []).join(' ') : String(c ?? ''));
function cardText(c) {
  const b = c.body || {};
  return [c.label, c.title, c.line,
    ...(b.points || []).flat(),
    ...(b.lists || []).flatMap((l) => [l.label, ...(l.items || [])]),
    ...(b.paras || []).flatMap((p) => [p.label, p.text])].join(' ');
}
const numberText = (r) => `${r.value} ${r.label} ${r.text}`;

// A chip's tag is in play if it is listed, always shown, or belongs to a listed
// one-letter Part (B1 ↔ B); Reg 7(2) does not belong to R.
function tagInPlay(tag, items, always) {
  if (always.includes(tag) || items.includes(tag)) return true;
  return !tag.startsWith('Reg') && items.some((i) => i.length === 1 && tag.startsWith(i));
}

/**
 * What the current filters do to each card and row.
 * → { cards: [{id, dim, hidden}], numbers: [{row, hidden}], tables: [{row, hidden}],
 *     facts: [{row, hidden}], lists: [{item, hidden}] }
 * in document order, over a resolved guide. Search hides; the segment and the chip
 * only dim a card (and hide numbers rows, which carry tags).
 */
export function applyFacets(guide, state) {
  const f = guide.facets || {};
  const ws = words(state?.q);
  const items = allBlocks(guide, 'cards').flatMap(({ block }) => block.items);
  // Typing a card's own letter means that card, not every "a" on the page.
  const exact = ws.length === 1 ? items.filter((c) => String(c.label).toLowerCase() === ws[0]) : [];
  const segKey = f.segment?.key;
  const seg = segKey ? state?.[segKey] : null;
  const chip = f.chips ? f.chips.options.find((o) => o.id === state?.[f.chips.key]) || null : null;
  const always = f.chips?.always || [];
  return {
    cards: items.map((c) => ({
      id: c.id,
      hidden: exact.length ? !exact.includes(c) : !matches(cardText(c), ws),
      dim: Boolean((seg && seg !== 'all' && c.facet?.[segKey] !== seg) || (chip && !chip.items.includes(c.id))),
    })),
    numbers: allBlocks(guide, 'numbers').flatMap(({ block }) => block.rows).map((row) => ({
      row,
      hidden: !matches(numberText(row), ws) || Boolean(chip && !(row.tags || []).some((t) => tagInPlay(t, chip.items, always))),
    })),
    tables: allBlocks(guide, 'table').flatMap(({ block }) => block.rows).map((row) => ({
      row,
      hidden: !matches(row.map(cellText).join(' '), ws),
    })),
    facts: allBlocks(guide, 'facts').flatMap(({ block }) => block.rows).map((row) => ({
      row,
      hidden: !matches(row.map(cellText).join(' '), ws),
    })),
    lists: allBlocks(guide, 'list').flatMap(({ block }) => block.items).map((item) => ({
      item,
      hidden: !matches(item, ws),
    })),
  };
}

const cardName = (c) => `${c.id.length === 1 ? `Part ${c.id}` : c.id} — ${c.title}`;

function sectionText(s) {
  return [s.title, ...(s.blocks || []).flatMap((b) => {
    if (b.kind === 'prose' || b.kind === 'reco') return [b.text];
    if (b.kind === 'kv') return b.rows.flat();
    if (b.kind === 'timeline') return b.rows.flatMap((r) => [r.title, r.text, ...(r.meta || []).flat()]);
    if (b.kind === 'sources') return b.items.flatMap((i) => [i.title, i.note]);
    return [];
  })].join(' ');
}

/**
 * Help search. `guides` are loaded guides, read from `perspective` where they
 * have it. Cards first, then numbers, table and facts rows and list lines (each
 * goes to its section), then any other words in a section.
 * → [{ guide, section, card, trail: ['Guides', title, where] }]
 */
export function searchHits(guides, q, perspective = null) {
  const ws = words(q);
  if (!ws.length) return [];
  const cards = [], rows = [], rest = [];
  for (const raw of guides) {
    const g = resolveGuide(raw, perspective);
    const trail = (where) => ['Guides', g.title, where];
    for (const s of g.sections || []) {
      for (const b of s.blocks || []) {
        if (b.kind === 'cards') {
          const exact = ws.length === 1 ? b.items.filter((c) => String(c.label).toLowerCase() === ws[0]) : [];
          for (const c of b.items) {
            if (exact.length ? exact.includes(c) : matches(cardText(c), ws)) cards.push({ guide: g.id, section: s.id, card: c.id, trail: trail(cardName(c)) });
          }
        } else if (b.kind === 'numbers') {
          for (const r of b.rows) if (matches(numberText(r), ws)) rows.push({ guide: g.id, section: s.id, card: null, trail: trail(`${s.title}: ${r.value}`) });
        } else if (b.kind === 'table') {
          for (const r of b.rows) if (matches(r.map(cellText).join(' '), ws)) rows.push({ guide: g.id, section: s.id, card: null, trail: trail(`${s.title}: ${cellText(r[0])}`) });
        } else if (b.kind === 'facts') {
          for (const r of b.rows) if (matches(r.map(cellText).join(' '), ws)) rows.push({ guide: g.id, section: s.id, card: null, trail: trail(`${s.title}: ${cellText(r[0])}`) });
        } else if (b.kind === 'list') {
          if (b.items.some((i) => matches(i, ws))) rows.push({ guide: g.id, section: s.id, card: null, trail: trail(`${s.title}: ${b.label}`) });
        }
      }
      if (matches(sectionText(s), ws)) rest.push({ guide: g.id, section: s.id, card: null, trail: trail(s.title) });
    }
  }
  return [...cards, ...rows, ...rest];
}

/** The URL of a guide, a section, or a card in it. */
export function guidePath(guide, section = null, card = null) {
  return '/guides' + [guide, section, card].filter(Boolean).map((p) => `/${encodeURIComponent(p)}`).join('');
}

/** The part of a guide URL after /guides, in the app's detail slot. */
export function guideDetail({ guide, section, card } = {}) {
  return [guide, section, card].filter(Boolean).join('/') || null;
}
/** The reverse: what a selectView('guides', …) opens. */
export function guideOpts(detailId) {
  const [guide = null, section = null, card = null] = String(detailId || '').split('/').filter(Boolean);
  return guide ? { guide, section, card } : {};
}

/** /guides[/guide[/section[/card]]] and /regs → the route app.js parsePath returns. */
export function parseGuidePath(pathname) {
  const parts = String(pathname || '').replace(/^\/+|\/+$/g, '').split('/');
  const seg = (parts[0] || '').toLowerCase();
  if (seg === 'regs') return { view: 'guides', detailId: 'building-regulations' };
  if (seg !== 'guides') return { view: null, detailId: null };
  return { view: 'guides', detailId: guideDetail(decoded(parts.slice(1))) };
}
function decoded(parts) {
  const [guide, section, card] = parts.map((p) => { try { return decodeURIComponent(p); } catch { return p; } });
  return { guide, section, card };
}

const WHERE_THE_PARTS_ARE = { guide: 'building-regulations', section: 'the-set' };
// Parts I and N do not exist, so they name nothing to link to.
const NAMES_A_PART = /\bPart\s+([A-HJ-MO-T])\b|\bApproved\s+Document\s+([A-HJ-MO-T])\b/i;
/**
 * Ask Track's quick reference link for a question, or null when it isn't about
 * the Building Regulations. A lower-case "part a" is ordinary English, so a Part
 * is named by its capital letter.
 */
export function quickReference(question) {
  const q = String(question || '');
  const m = q.match(NAMES_A_PART);
  const letter = m ? (m[1] || m[2]) : null;
  if (letter && letter === letter.toUpperCase()) return { ...WHERE_THE_PARTS_ARE, card: letter, label: `Quick reference: Part ${letter}` };
  if (/approved document|building reg/i.test(q)) return { guide: WHERE_THE_PARTS_ARE.guide, section: null, card: null, label: 'Quick reference: Building Regulations' };
  return null;
}
